/**
 * Dynamic Field Toggle Component
 * Provides a dropdown interface to show/hide invoice item fields based on scenario
 */

import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const DynamicFieldToggle = ({ 
  scenarioId, 
  fieldConfig = {}, 
  visibleFields = {}, 
  onFieldVisibilityChange,
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localVisibleFields, setLocalVisibleFields] = useState(visibleFields);

  // All available fields with their display information
  const allFields = {
    rate: { 
      label: 'Tax Rate', 
      description: 'Sales tax rate percentage',
      required: true 
    },
    uoM: { 
      label: 'Unit of Measure', 
      description: 'Unit of measurement for the item',
      required: true 
    },
    fixedNotifiedValueOrRetailPrice: { 
      label: 'Fixed Notified Value / Retail Price', 
      description: 'Printed retail price for 3rd Schedule goods (SN008: integer only, SN027: allows decimals)',
      required: false 
    },
    salesTaxWithheldAtSource: { 
      label: 'Sales Tax Withheld at Source', 
      description: 'Tax amount withheld at source',
      required: false 
    },
    extraTax: { 
      label: 'Extra Tax', 
      description: 'Additional tax amount',
      required: false 
    },
    furtherTax: { 
      label: 'Further Tax', 
      description: 'Further tax amount',
      required: false 
    },
    sroScheduleNo: { 
      label: 'SRO Schedule No', 
      description: 'SRO schedule number reference',
      required: false 
    },
    fedPayable: { 
      label: 'FED Payable', 
      description: 'Federal Excise Duty payable',
      required: false 
    },
    discount: { 
      label: 'Discount', 
      description: 'Discount percentage or amount',
      required: false 
    },
    saleType: { 
      label: 'Sale Type', 
      description: 'Type of sale transaction',
      required: true 
    },
    sroItemSerialNo: { 
      label: 'SRO Item Serial No', 
      description: 'SRO item serial number',
      required: false 
    }
  };

  // Update local state when props change
  useEffect(() => {
    setLocalVisibleFields(visibleFields);
  }, [visibleFields]);

  // Handle field visibility toggle
  const handleFieldToggle = (fieldName) => {
    const newVisibleFields = {
      ...localVisibleFields,
      [fieldName]: !localVisibleFields[fieldName]
    };
    
    setLocalVisibleFields(newVisibleFields);
    
    if (onFieldVisibilityChange) {
      onFieldVisibilityChange(newVisibleFields);
    }
  };

  // Get field status (visible, has data, required)
  const getFieldStatus = (fieldName) => {
    const config = fieldConfig[fieldName] || {};
    const isVisible = localVisibleFields[fieldName];
    const hasData = config.hasData || false;
    const isRequired = allFields[fieldName]?.required || false;
    
    return {
      isVisible,
      hasData,
      isRequired,
      defaultValue: config.defaultValue
    };
  };

  // Count visible fields
  const visibleCount = Object.values(localVisibleFields).filter(Boolean).length;
  const totalCount = Object.keys(allFields).length;

  return (
    <div className={`relative ${className}`}>
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <div className="flex items-center space-x-2">
          <span>Configure Fields</span>
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
            {visibleCount}/{totalCount}
          </span>
          {scenarioId && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
              {scenarioId}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">
              Field Visibility Settings
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Toggle fields based on scenario {scenarioId} requirements
            </p>
          </div>
          
          <div className="p-2 space-y-1">
            {Object.entries(allFields).map(([fieldName, fieldInfo]) => {
              const status = getFieldStatus(fieldName);
              
              return (
                <div
                  key={fieldName}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id={`field-${fieldName}`}
                      checked={status.isVisible}
                      onChange={() => handleFieldToggle(fieldName)}
                      disabled={status.isRequired}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={`field-${fieldName}`}
                        className={`text-sm font-medium ${
                          status.isRequired ? 'text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {fieldInfo.label}
                        {status.isRequired && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500">
                        {fieldInfo.description}
                      </p>
                      {status.hasData && status.defaultValue !== undefined && (
                        <p className="text-xs text-blue-600 mt-1">
                          Default: {status.defaultValue || 'Empty'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    {status.isRequired && (
                      <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                        Required
                      </span>
                    )}
                    {status.hasData && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        Has Data
                      </span>
                    )}
                    {status.isVisible && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        Visible
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                {visibleCount} of {totalCount} fields visible
              </span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicFieldToggle;
